import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'antd'
import { ExpandOutlined, CompressOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { API } from 'src/utils/interceptor'
import config from 'src/config'

const PREVIEW_ZOOM_MIN = 1
const PREVIEW_ZOOM_MAX = 4
const PREVIEW_ZOOM_STEP = 0.1
const PREVIEW_BASE_HEIGHT = 400
/** Phase 3: taller preview while staying inside the attach modal. */
const PREVIEW_EXPANDED_HEIGHT = 720

function clampPreviewZoom(value) {
  return Math.min(
    PREVIEW_ZOOM_MAX,
    Math.max(PREVIEW_ZOOM_MIN, Math.round(value * 10) / 10),
  )
}

function isPdfUrl(url = '') {
  const value = String(url || '').toLowerCase()
  return value.includes('.pdf') || value.includes('application/pdf')
}

/**
 * 100% = fit page to preview width (FitH).
 * Zoomed levels use browser PDF #zoom=N (sharp, no CSS stretch).
 */
function buildPdfViewerSrc(url, zoom) {
  const base = String(url || '').split('#')[0]
  if (!base) return ''
  const clamped = clampPreviewZoom(zoom)
  if (clamped <= PREVIEW_ZOOM_MIN) {
    return `${base}#view=FitH`
  }
  return `${base}#zoom=${Math.round(clamped * 100)}`
}

function buildFileUrlFromKey(key) {
  if (!key) return ''
  const base = String(config.apiGateway.EXPRESS_URL || '').replace(/\/$/, '')
  return `${base}/files/${encodeURIComponent(key)}`
}

/**
 * Primary file + additionalPages (same shape InvoiceView / listInvoiceDocumentPages use).
 */
function buildPagesFromInvoice(invoice, fallbackUrl) {
  if (!invoice) {
    return fallbackUrl
      ? [{ key: 'primary', url: fallbackUrl, label: 'Page 1' }]
      : []
  }

  const pages = []
  const primaryUrl =
    fallbackUrl ||
    invoice.invoiceUrl ||
    invoice.invoiceTempUrl ||
    buildFileUrlFromKey(invoice.key)

  if (primaryUrl) {
    pages.push({
      key: invoice.key || 'primary',
      url: primaryUrl,
      label: 'Page 1',
      originalFilename: invoice.originalFilename || '',
    })
  }

  for (const [index, page] of (invoice.additionalPages || []).entries()) {
    const pageUrl =
      page?.invoiceUrl || page?.invoiceTempUrl || buildFileUrlFromKey(page?.key)
    if (!pageUrl) continue
    pages.push({
      key: page?.key || `page-${index + 2}`,
      url: pageUrl,
      label: `Page ${pages.length + 1}`,
      originalFilename: page?.originalFilename || '',
    })
  }

  if (pages.length) return pages
  return fallbackUrl
    ? [{ key: 'primary', url: fallbackUrl, label: 'Page 1' }]
    : []
}

/**
 * Goods Receipting attached-invoice preview.
 * Phase 1: OMS-style +/- / Reset, pan/scroll, Download, Open PDF in new tab.
 * Phase 2: multi-page navigation via additionalPages (InvoiceView-style switcher).
 * Phase 3: Fit (re-apply width fit) + Expand (taller preview in-modal; no navigation away).
 * Images: width zoom. PDFs: browser viewer FitH / #zoom (no PDF.js).
 */
function AttachedInvoicePreview({
  url,
  pages: pagesProp,
  alt = 'Attached invoice',
  downloadLabel,
  onDownload,
}) {
  const [zoom, setZoom] = useState(PREVIEW_ZOOM_MIN)
  const [fitNonce, setFitNonce] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const [pages, setPages] = useState(() =>
    url ? [{ key: 'primary', url, label: 'Page 1' }] : [],
  )
  const [activePageIndex, setActivePageIndex] = useState(0)

  useEffect(() => {
    let cancelled = false
    setActivePageIndex(0)
    setZoom(PREVIEW_ZOOM_MIN)
    setFitNonce(0)
    setExpanded(false)

    if (Array.isArray(pagesProp) && pagesProp.length > 0) {
      setPages(pagesProp)
      return () => {
        cancelled = true
      }
    }

    if (!url) {
      setPages([])
      return () => {
        cancelled = true
      }
    }

    // Show the given URL immediately; enrich with additionalPages if invoice exists.
    setPages([{ key: 'primary', url, label: 'Page 1' }])
    ;(async () => {
      try {
        const invoiceResponse = await API.get(
          'api2',
          'invoices/getInvoicebyUrl',
          {
            queryStringParameters: { invoiceUrl: url },
          },
        )
        if (cancelled) return
        if (invoiceResponse?.success && invoiceResponse?.data) {
          const nextPages = buildPagesFromInvoice(invoiceResponse.data, url)
          if (nextPages.length > 0) {
            setPages(nextPages)
          }
        }
      } catch (err) {
        // Keep single-page fallback from prop url — do not break preview.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [url, pagesProp])

  const activePage = pages[activePageIndex] || pages[0] || null
  const activeUrl = activePage?.url || url || ''
  const isPdf = isPdfUrl(activeUrl)
  const clampedZoom = clampPreviewZoom(zoom)
  const previewHeight = expanded ? PREVIEW_EXPANDED_HEIGHT : PREVIEW_BASE_HEIGHT
  const pdfSrc = useMemo(
    () => (isPdf ? buildPdfViewerSrc(activeUrl, clampedZoom) : ''),
    [isPdf, activeUrl, clampedZoom],
  )
  const showPageSwitcher = pages.length > 1

  const selectPage = index => {
    setActivePageIndex(index)
    setZoom(PREVIEW_ZOOM_MIN)
    setFitNonce(n => n + 1)
  }

  const fitToWidth = () => {
    setZoom(PREVIEW_ZOOM_MIN)
    setFitNonce(n => n + 1)
  }

  if (!activeUrl) {
    return (
      <FilePreview>
        <PreviewPlaceholder>Preview unavailable</PreviewPlaceholder>
      </FilePreview>
    )
  }

  const adjustZoom = delta => {
    setZoom(prev => clampPreviewZoom(prev + delta))
  }

  return (
    <FilePreview $expanded={expanded}>
      <PreviewToolbar>
        <ZoomControls>
          <ZoomButton
            type='default'
            size='small'
            disabled={clampedZoom <= PREVIEW_ZOOM_MIN}
            onClick={() => adjustZoom(-PREVIEW_ZOOM_STEP)}
            aria-label='Zoom out'
          >
            −
          </ZoomButton>
          <ZoomLevel>{Math.round(clampedZoom * 100)}%</ZoomLevel>
          <ZoomButton
            type='default'
            size='small'
            disabled={clampedZoom >= PREVIEW_ZOOM_MAX}
            onClick={() => adjustZoom(PREVIEW_ZOOM_STEP)}
            aria-label='Zoom in'
          >
            +
          </ZoomButton>
          <ZoomResetButton
            type='link'
            size='small'
            onClick={fitToWidth}
            aria-label='Fit to width'
          >
            {clampedZoom > PREVIEW_ZOOM_MIN ? 'Reset / Fit' : 'Fit'}
          </ZoomResetButton>
        </ZoomControls>
        <ToolbarActions>
          <ExpandToggleButton
            type='default'
            size='small'
            onClick={() => setExpanded(prev => !prev)}
            aria-label={expanded ? 'Collapse preview' : 'Expand preview'}
          >
            {expanded ? <CompressOutlined /> : <ExpandOutlined />}
            {expanded ? 'Collapse' : 'Expand'}
          </ExpandToggleButton>
          {isPdf && (
            <OpenPdfLink
              href={activeUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              Open PDF in new tab
            </OpenPdfLink>
          )}
          {typeof onDownload === 'function' && (
            <Button
              size='small'
              icon='download'
              onClick={() => onDownload(activeUrl)}
            >
              {downloadLabel || 'Download'}
            </Button>
          )}
        </ToolbarActions>
      </PreviewToolbar>

      {showPageSwitcher && (
        <PageSwitcher>
          {pages.map((page, index) => (
            <Button
              key={page.key || `page-${index}`}
              size='small'
              type={index === activePageIndex ? 'primary' : 'default'}
              onClick={() => selectPage(index)}
            >
              {page.label || `Page ${index + 1}`}
            </Button>
          ))}
        </PageSwitcher>
      )}

      <PreviewViewport style={{ maxHeight: previewHeight }}>
        {isPdf ? (
          <PdfPreviewFrame
            key={`pdf-${activePageIndex}-zoom-${Math.round(
              clampedZoom * 100,
            )}-fit-${fitNonce}`}
            title={activePage?.label || alt}
            src={pdfSrc}
            style={{ height: previewHeight }}
          />
        ) : (
          <PreviewImage
            key={`img-${activePageIndex}`}
            src={activeUrl}
            alt={activePage?.label || alt}
            style={{ width: `${clampedZoom * 100}%` }}
          />
        )}
      </PreviewViewport>
    </FilePreview>
  )
}

export default AttachedInvoicePreview

const FilePreview = styled.div`
  position: relative;
  width: 100%;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
  ${props =>
    props.$expanded
      ? `
    box-shadow: 0 0 0 1px #d9f7be;
  `
      : ''}
`

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  min-height: 32px;
  flex-wrap: wrap;
`

const ZoomControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const ZoomButton = styled(Button)`
  && {
    width: 28px;
    min-width: 28px;
    height: 28px;
    padding: 0;
    font-size: 16px;
    font-weight: 600;
  }
`

const ZoomResetButton = styled(Button)`
  && {
    padding: 0 4px;
    height: 28px;
  }
`

const ExpandToggleButton = styled(Button)`
  && {
    font-size: 12px;
  }
`

const ZoomLevel = styled.span`
  min-width: 44px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
`

const ToolbarActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  flex-wrap: wrap;
`

const OpenPdfLink = styled.a`
  font-size: 13px;
  font-weight: 500;
  color: #389e0d;
`

const PageSwitcher = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`

const PreviewViewport = styled.div`
  width: 100%;
  overflow: auto;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #f0f0f0;
`

const PreviewImage = styled.img`
  display: block;
  max-width: none;
  height: auto;
  margin: 0 auto;
`

const PdfPreviewFrame = styled.iframe`
  display: block;
  width: 100%;
  border: none;
  background: #fff;
`

const PreviewPlaceholder = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-size: 13px;
  color: #8c8c8c;
  background: #fff;
`
